import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Box, IconButton, Typography, Modal, Chip, CircularProgress } from '@mui/material';
import {
    Close as CloseIcon,
    ChevronLeft as PrevIcon,
    ChevronRight as NextIcon,
} from '@mui/icons-material';

const STAGE_LABELS = {
    INITIAL: { label: 'Initial', color: 'primary' },
    IN_PROGRESS: { label: 'In Progress', color: 'warning' },
    COMPLETED: { label: 'Completed', color: 'success' },
};

/**
 * ImagePreviewModal - Full-screen image preview with navigation.
 * Fetches fresh signed URLs on demand via onRequestUrl callback.
 * 
 * @param {boolean} open
 * @param {function} onClose
 * @param {Array} images - Array of image objects { id, url, thumbnailUrl, stage, uploadedAt, caption }
 * @param {number} initialIndex
 * @param {function} [onRequestUrl] - Async callback(imageId) => { url, thumbnailUrl }
 */
export default function ImagePreviewModal({
    open,
    onClose,
    images = [],
    initialIndex = 0,
    onRequestUrl,
}) {
    const [currentIndex, setCurrentIndex] = useState(initialIndex);
    const [touchStart, setTouchStart] = useState(null);
    const [touchEnd, setTouchEnd] = useState(null);

    // Cache of fresh full-size URLs: { [imageId]: { url, loading, error } }
    const [urlCache, setUrlCache] = useState({});
    const fetchingRef = useRef(new Set());

    // Reset to initial index and clear cache when modal opens
    useEffect(() => {
        if (open) {
            setCurrentIndex(initialIndex);
            setUrlCache({});
            fetchingRef.current.clear();
        }
    }, [open, initialIndex]);

    const currentImage = images[currentIndex];

    // Fetch fresh URL for the current image if onRequestUrl is provided
    useEffect(() => {
        if (!open || !currentImage || !onRequestUrl) return;
        const imageId = currentImage.id;
        if (!imageId || urlCache[imageId] || fetchingRef.current.has(imageId)) return;

        fetchingRef.current.add(imageId);
        setUrlCache(prev => ({ ...prev, [imageId]: { url: null, loading: true, error: false } }));

        onRequestUrl(imageId)
            .then(({ url }) => {
                setUrlCache(prev => ({ ...prev, [imageId]: { url, loading: false, error: false } }));
            })
            .catch(() => {
                setUrlCache(prev => ({ ...prev, [imageId]: { url: null, loading: false, error: true } }));
            })
            .finally(() => {
                fetchingRef.current.delete(imageId);
            });
    }, [open, currentIndex, currentImage, onRequestUrl, urlCache]);

    // Resolve the best URL for the current image
    const resolvedUrl = (() => {
        if (!currentImage) return null;
        const cached = urlCache[currentImage.id];
        if (cached?.url) return cached.url;
        return currentImage.url;
    })();
    const isLoadingUrl = currentImage && urlCache[currentImage.id]?.loading;

    const goToPrevious = useCallback(() => {
        setCurrentIndex((prev) => (prev > 0 ? prev - 1 : images.length - 1));
    }, [images.length]);

    const goToNext = useCallback(() => {
        setCurrentIndex((prev) => (prev < images.length - 1 ? prev + 1 : 0));
    }, [images.length]);

    // Handle keyboard navigation
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (!open) return;
            if (e.key === 'ArrowLeft') goToPrevious();
            if (e.key === 'ArrowRight') goToNext();
            if (e.key === 'Escape') onClose();
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [open, goToPrevious, goToNext, onClose]);

    // Swipe detection threshold (in pixels)
    const minSwipeDistance = 50;

    const handleTouchStart = (e) => {
        setTouchEnd(null);
        setTouchStart(e.targetTouches[0].clientX);
    };

    const handleTouchMove = (e) => {
        setTouchEnd(e.targetTouches[0].clientX);
    };

    const handleTouchEnd = () => {
        if (!touchStart || !touchEnd) return;
        const distance = touchStart - touchEnd;
        if (distance > minSwipeDistance) goToNext();
        else if (distance < -minSwipeDistance) goToPrevious();
    };

    if (images.length === 0) return null;

    return (
        <Modal
            open={open}
            onClose={onClose}
            sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
        >
            <Box
                sx={{
                    position: 'relative',
                    width: '100%',
                    height: '100%',
                    bgcolor: 'rgba(0, 0, 0, 0.95)',
                    outline: 'none',
                }}
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}
            >
                {/* Close Button */}
                <IconButton
                    onClick={onClose}
                    sx={{
                        position: 'absolute',
                        top: 16,
                        right: 16,
                        color: 'white',
                        bgcolor: 'rgba(0, 0, 0, 0.5)',
                        zIndex: 10,
                        '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.7)' },
                    }}
                >
                    <CloseIcon />
                </IconButton>

                {/* Image Counter */}
                <Typography
                    variant="body2"
                    sx={{
                        position: 'absolute',
                        top: 24,
                        left: '50%',
                        transform: 'translateX(-50%)',
                        color: 'white',
                        bgcolor: 'rgba(0, 0, 0, 0.5)',
                        px: 2,
                        py: 0.5,
                        borderRadius: 2,
                        zIndex: 10,
                    }}
                >
                    {currentIndex + 1} / {images.length}
                </Typography>

                {/* Navigation Buttons */}
                {images.length > 1 && (
                    <>
                        <IconButton
                            onClick={goToPrevious}
                            sx={{
                                position: 'absolute',
                                left: 8,
                                top: '50%',
                                transform: 'translateY(-50%)',
                                color: 'white',
                                bgcolor: 'rgba(0, 0, 0, 0.5)',
                                zIndex: 10,
                                '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.7)' },
                            }}
                        >
                            <PrevIcon />
                        </IconButton>
                        <IconButton
                            onClick={goToNext}
                            sx={{
                                position: 'absolute',
                                right: 8,
                                top: '50%',
                                transform: 'translateY(-50%)',
                                color: 'white',
                                bgcolor: 'rgba(0, 0, 0, 0.5)',
                                zIndex: 10,
                                '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.7)' },
                            }}
                        >
                            <NextIcon />
                        </IconButton>
                    </>
                )}

                {/* Main Image */}
                <Box
                    sx={{
                        position: 'absolute',
                        top: 0, left: 0, right: 0, bottom: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        p: 2,
                    }}
                >
                    {isLoadingUrl ? (
                        <CircularProgress sx={{ color: 'white' }} />
                    ) : (
                        <Box
                            component="img"
                            src={resolvedUrl}
                            alt={`Photo ${currentIndex + 1}`}
                            sx={{
                                maxWidth: '100%',
                                maxHeight: '100%',
                                objectFit: 'contain',
                            }}
                        />
                    )}
                </Box>

                {/* Bottom Metadata Bar */}
                {currentImage && (
                    <Box
                        sx={{
                            position: 'absolute',
                            bottom: 0,
                            left: 0,
                            right: 0,
                            bgcolor: 'rgba(0, 0, 0, 0.7)',
                            color: 'white',
                            px: 2,
                            py: 1.5,
                            zIndex: 10,
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 0.5,
                        }}
                    >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                            {currentImage.stage && STAGE_LABELS[currentImage.stage] && (
                                <Chip
                                    label={STAGE_LABELS[currentImage.stage].label}
                                    color={STAGE_LABELS[currentImage.stage].color}
                                    size="small"
                                    sx={{ height: 22, fontSize: '0.7rem' }}
                                />
                            )}
                            {currentImage.uploadedAt && (
                                <Typography variant="caption" sx={{ opacity: 0.8 }}>
                                    {new Date(currentImage.uploadedAt).toLocaleString()}
                                </Typography>
                            )}
                        </Box>
                        {currentImage.caption && (
                            <Typography variant="body2">
                                {currentImage.caption}
                            </Typography>
                        )}
                    </Box>
                )}
            </Box>
        </Modal>
    );
}
